<template>
  <div v-if="visible" class="config-dialog">
    <div class="dialog-content">
      <div class="dialog-header-inner">
        <div class="small-title">
          <span>{{ dialogTitle }}</span>
        </div>
      </div>
      <div v-if="hasDialogPermission" class="dialog-body-content">
        <div class="midpoint-settings-card">
          <!-- 催贵公式表格 -->
          <div class="midpoint-section">
            <div class="table-container">
              <table class="midpoint-table">
                <thead>
                  <tr>
                    <th class="corner-cell sticky-cell"></th>
                    <th
                      v-for="(col, index) in columnHeaders"
                      :key="'col-'+index"
                      class="midpoint-header-cell sticky-cell"
                      :class="{ 'selected': isColumnSelected(index) }"
                    >
                      {{ col.label }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, rowIndex) in rowHeaders"
                      :key="'row-'+rowIndex">
                    <td
                      class="row-label"
                      :class="{ 'row-selected': isRowSelected(rowIndex) }"
                    >{{ row.label }}</td>
                    <td
                      v-for="(col, colIndex) in columnHeaders"
                      :key="'cell-'+rowIndex+'-'+colIndex"
                      class="midpoint-cell"
                      :class="{ 'cell-selected': isCellSelected(rowIndex, colIndex), 'cell-diagonal': rowIndex === colIndex }"
                      @click="toggleCell(rowIndex, colIndex)"
                    >
                      <span v-if="isCellSelected(rowIndex, colIndex)" class="cell-content">
                        {{ getStdPairName(rowIndex, colIndex) }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="selection-hint">
              <p>提示：选择行星对后，将计算这些行星对的中点位置并显示在星盘上</p>
            </div>
          </div>
        </div>
      </div>
      <div class="dialog-footer">
        <div class="dialog-buttons">
          <button class="btn-cancel" @click="cancel">
            取消
          </button>
          <button class="btn-confirm" @click="confirm">
            确定
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PermissionService from '@/services/PermissionService'

export default {
  name: 'MidpointConfigDialog',
  props: {
    visible: Boolean,
    planets: {
      type: Array,
      default: () => []
    },
    // 已选中的中点数组，用于恢复选择状态
    selectedMidpoints: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      // 列表头（与行表头相同）
      columnHeaders: [],
      // 行表头
      rowHeaders: [],
      // 列选择状态
      columnSelections: [],
      // 行选择状态
      rowSelections: [],
      // 单元格选择状态（二维数组）
      cellSelections: []
    }
  },
  computed: {
    dialogTitle () {
      return '催贵公式设置'
    },
    hasDialogPermission () {
      return PermissionService.hasPermission('midpoint_formula')
    }
  },
  watch: {
    visible (newVal) {
      if (newVal) {
        this.initHeaders()
        // 恢复之前保存的选择状态
        this.restoreSelections()
      }
    },
    planets: {
      immediate: true,
      deep: true,
      handler () {
        if (this.visible) {
          this.initHeaders()
          this.restoreSelections()
        }
      }
    },
    // 监听已选中的中点变化
    selectedMidpoints: {
      immediate: true,
      handler () {
        if (this.visible) {
          this.restoreSelections()
        }
      }
    }
  },
  methods: {
    // 初始化表头
    initHeaders () {
      if (!this.hasDialogPermission) {
        this.rowHeaders = []
        this.columnHeaders = []
        this.columnSelections = []
        this.rowSelections = []
        this.cellSelections = []
        return
      }

      // 过滤出有效的行星（与PlanetConfigDialog保持一致的顺序）
      const planetNameMap = {
        0: { label: '太阳', shortLabel: '日' },
        1: { label: '月亮', shortLabel: '月' },
        2: { label: '水星', shortLabel: '水' },
        3: { label: '金星', shortLabel: '金' },
        4: { label: '火星', shortLabel: '火' },
        5: { label: '木星', shortLabel: '木' },
        6: { label: '土星', shortLabel: '土' },
        7: { label: '天王星', shortLabel: '天' },
        8: { label: '海王星', shortLabel: '海' },
        9: { label: '冥王星', shortLabel: '冥' },
        11: { label: '计都', shortLabel: '计' },
        12: { label: '谷神星', shortLabel: '谷' },
        13: { label: '月孛', shortLabel: '孛' },
        15: { label: '凯龙星', shortLabel: '凯' },
        1000012: { label: '紫炁', shortLabel: '紫' },
        1000013: { label: '罗睺', shortLabel: '罗' },
        1000014: { label: '中天', shortLabel: 'MC' },
        1000015: { label: '上升', shortLabel: 'ASC' },
        1000016: { label: '福点', shortLabel: '福' },
        1000017: { label: '宿命点', shortLabel: '宿' },
        1000018: { label: '反宿命点', shortLabel: '反' },
        1000019: { label: '春分点', shortLabel: '春' },
        40: { label: '丘比特', shortLabel: '丘' },
        41: { label: '哈德斯', shortLabel: '哈' },
        42: { label: '宙斯', shortLabel: '宙' },
        43: { label: '克洛诺斯', shortLabel: '克' },
        44: { label: '太阳神', shortLabel: '太' },
        45: { label: '阿德门图斯', shortLabel: '阿' },
        46: { label: '弗卡奴斯', shortLabel: '弗' },
        47: { label: '波塞东', shortLabel: '波' },
        11181: { label: '莉莉丝', shortLabel: '莉莉' }
      }

      // 按照固定顺序排列 (从冥开始之后依次是：上升、中天、罗睺、计都、紫炁、月孛、福点、宿命、反宿命、春分、凯龙、莉莉丝、谷神星、丘比特等)
      const orderedPlanetIds = [
        0, 1, 2, 3, 4, 5, 6, 7, 8, 9,
        1000015, 1000014, 1000013, 11, 1000012, 13, 1000016, 1000017, 1000018, 1000019, 15, 11181, 12,
        40, 41, 42, 43, 44, 45, 46, 47
      ]

      const headers = []
      orderedPlanetIds.forEach(id => {
        const nameInfo = planetNameMap[id]
        if (nameInfo) {
          headers.push({
            id: id,
            label: nameInfo.label,
            shortLabel: nameInfo.shortLabel
          })
        }
      })

      this.rowHeaders = headers
      this.columnHeaders = headers

      // 权限过滤：虚星
      if (!this.hasPermission('asteroids')) {
        const ASTEROID_IDS = new Set([40, 41, 42, 43, 44, 45, 46, 47])
        this.rowHeaders = this.rowHeaders.filter(h => !ASTEROID_IDS.has(h.id))
        this.columnHeaders = this.columnHeaders.filter(h => !ASTEROID_IDS.has(h.id))
      }

      // 初始化列选择状态
      this.columnSelections = Array(headers.length).fill(false)

      // 初始化行选择状态
      this.rowSelections = Array(headers.length).fill(false)

      // 初始化单元格选择状态
      this.cellSelections = Array(headers.length).fill(null).map(() => Array(headers.length).fill(false))
    },

    // 恢复之前保存的选择状态
    restoreSelections () {
      if (!this.selectedMidpoints || this.selectedMidpoints.length === 0) {
        return
      }

      // 根据已选中的行星对恢复单元格选择状态
      this.selectedMidpoints.forEach(pair => {
        // 找到行星在 rowHeaders 和 columnHeaders 中的索引
        const rowIndex = this.rowHeaders.findIndex(h => h.id === pair.planet1Index)
        const colIndex = this.columnHeaders.findIndex(h => h.id === pair.planet2Index)

        if (rowIndex !== -1 && colIndex !== -1) {
          // 计算等价的上三角坐标
          const equiv = this.getEquivalentCell(rowIndex, colIndex)
          if (!equiv) return

          const stdRow = equiv.row
          const stdCol = equiv.col

          // 上三角和下三角的位置
          const upperRow = stdRow
          const upperCol = stdCol
          const lowerRow = stdCol
          const lowerCol = stdRow

          // 选中上三角位置的单元格
          this.$set(this.cellSelections, upperRow, [...(this.cellSelections[upperRow] || [])])
          this.$set(this.cellSelections[upperRow], upperCol, true)

          // 选中下三角位置的单元格
          this.$set(this.cellSelections, lowerRow, [...(this.cellSelections[lowerRow] || [])])
          this.$set(this.cellSelections[lowerRow], lowerCol, true)

          // 同时选中相关的行和列
          this.$set(this.rowSelections, upperRow, true)
          this.$set(this.rowSelections, lowerRow, true)
          this.$set(this.columnSelections, upperCol, true)
          this.$set(this.columnSelections, lowerCol, true)
        }
      })
    },

    // 判断行是否被选中
    isRowSelected (rowIndex) {
      return this.rowSelections[rowIndex] || false
    },

    // 判断列是否被选中
    isColumnSelected (colIndex) {
      return this.columnSelections[colIndex] || false
    },

    // 切换列选择状态
    toggleColumn (colIndex) {
      const newState = !this.columnSelections[colIndex]
      this.$set(this.columnSelections, colIndex, newState)

      // 如果列被取消选中，重置该列所有单元格
      if (!newState) {
        for (let row = 0; row < this.rowHeaders.length; row++) {
          this.$set(this.cellSelections[row], colIndex, false)
        }
      }
    },

    // 判断单元格是否被选中
    isCellSelected (rowIndex, colIndex) {
      return this.cellSelections[rowIndex] && this.cellSelections[rowIndex][colIndex] === true
    },

    // 获取等价单元格坐标（上三角：row < col）
    getEquivalentCell (rowIndex, colIndex) {
      // 如果是上三角，直接返回
      if (rowIndex < colIndex) {
        return { row: rowIndex, col: colIndex }
      }
      // 如果是下三角，返回等价的上三角位置
      if (rowIndex > colIndex) {
        return { row: colIndex, col: rowIndex }
      }
      return null // 对角线
    },

    // 获取标准配对名称（上三角格式）
    getStdPairName (rowIndex, colIndex) {
      const equiv = this.getEquivalentCell(rowIndex, colIndex)
      if (!equiv) return ''
      const rowPlanet = this.rowHeaders[equiv.row]
      const colPlanet = this.columnHeaders[equiv.col]
      return `${rowPlanet.shortLabel}-${colPlanet.shortLabel}`
    },

    // 切换单元格选择状态
    toggleCell (rowIndex, colIndex) {
      // 不能选择同一个行星（对角线）
      if (rowIndex === colIndex) {
        return
      }

      // 计算等价的上三角坐标
      const equiv = this.getEquivalentCell(rowIndex, colIndex)
      if (!equiv) return

      const stdRow = equiv.row
      const stdCol = equiv.col

      // 上三角和下三角的位置
      const upperRow = stdRow
      const upperCol = stdCol
      const lowerRow = stdCol
      const lowerCol = stdRow

      // 当前状态（检查上三角位置的状态）
      const currentState = this.cellSelections[upperRow][upperCol]

      // 切换状态
      const newState = !currentState

      // 更新上三角位置的单元格
      this.$set(this.cellSelections, upperRow, [...(this.cellSelections[upperRow] || [])])
      this.$set(this.cellSelections[upperRow], upperCol, newState)

      // 更新下三角位置的单元格
      this.$set(this.cellSelections, lowerRow, [...(this.cellSelections[lowerRow] || [])])
      this.$set(this.cellSelections[lowerRow], lowerCol, newState)

      // 如果单元格被选中，同时选中相关的行和列
      if (newState) {
        // 选中所有相关的行和列
        this.$set(this.rowSelections, upperRow, true)
        this.$set(this.rowSelections, lowerRow, true)
        this.$set(this.columnSelections, upperCol, true)
        this.$set(this.columnSelections, lowerCol, true)
      } else {
        // 如果取消选中，检查相关行是否还有其他选中的单元格
        const checkRowSelected = (r) => {
          return this.cellSelections[r].some((state, idx) => state === true)
        }

        if (!checkRowSelected(upperRow)) {
          this.$set(this.rowSelections, upperRow, false)
        }
        if (!checkRowSelected(lowerRow)) {
          this.$set(this.rowSelections, lowerRow, false)
        }

        const checkColSelected = (c) => {
          return this.cellSelections.some((row, idx) => row[c] === true)
        }

        if (!checkColSelected(upperCol)) {
          this.$set(this.columnSelections, upperCol, false)
        }
        if (!checkColSelected(lowerCol)) {
          this.$set(this.columnSelections, lowerCol, false)
        }
      }
    },

    // 确认保存
    confirm () {
      if (!this.hasDialogPermission) {
        this.$emit('update:visible', false)
        return
      }

      // 收集选中的行星对（只取上三角：row < col）
      const selectedPairs = []

      for (let row = 0; row < this.cellSelections.length; row++) {
        for (let col = row + 1; col < this.cellSelections[row].length; col++) {
          if (this.cellSelections[row][col] === true) {
            const rowPlanet = this.rowHeaders[row]
            const colPlanet = this.columnHeaders[col]
            selectedPairs.push({
              planet1Index: rowPlanet.id,
              planet1Name: rowPlanet.label,
              planet2Index: colPlanet.id,
              planet2Name: colPlanet.label
            })
          }
        }
      }

      this.$emit('save', selectedPairs)
      this.$emit('update:visible', false)
    },

    cancel () {
      this.$emit('update:visible', false)
    },
    hasPermission (key) {
      return PermissionService.hasPermission(key)
    }
  }
}
</script>

<style scoped>
.config-dialog {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.dialog-content {
  width: 1100px;
  max-height: 90vh;
  background: white;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25);
}

.dialog-header-inner {
  background: #f0f4f8;
  border-bottom: 1px solid #e2e8f0;
  padding: 8px 24px;
}

.small-title {
  font-size: 1.1rem;
  font-weight: 500;
  color: #4a5568;
  padding: 4px 0;
}

.dialog-body-content {
  padding: 16px 24px;
  flex: 1;
  overflow: hidden;
}

.midpoint-settings-card {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.table-container {
  overflow: auto;
  max-width: 100%;
  max-height: calc(90vh - 200px);
  margin: 8px 0;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
}

.midpoint-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 1000px;
}

.midpoint-table th,
.midpoint-table td {
  padding: 8px 12px;
  text-align: center;
  font-size: 0.85rem;
  border: 1px solid #e2e8f0;
  white-space: nowrap;
}

.midpoint-table th {
  background-color: #edf2f7;
  font-weight: 600;
  min-width: 60px;
}

/* 表头单元格样式 */
.midpoint-header-cell {
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  padding: 10px 8px;
}

.midpoint-header-cell:hover {
  background-color: #d1e8f5;
}

.midpoint-header-cell.selected {
  background-color: #3182ce;
  color: white;
  box-shadow: 0 2px 4px rgba(49, 130, 206, 0.3);
}

/* 单元格样式 */
.midpoint-cell {
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 32px;
  width: 60px;
  font-weight: 400;
}

/* 选中的单元格 */
.midpoint-cell.cell-selected {
  background-color: #e6f7ff;
}

.midpoint-cell:hover:not(.cell-selected) {
  background-color: #d1e8f5;
}

/* 对角线格子样式（禁止选择） */
.midpoint-cell.cell-diagonal {
  background-color: #e2e8f0;
  cursor: not-allowed;
}

.midpoint-cell.cell-diagonal:hover {
  background-color: #e2e8f0;
}

.cell-content {
  font-size: 0.85rem;
  color: #2c5282;
  font-weight: 400;
}

.corner-cell {
  background-color: #edf2f7 !important;
  border-right: 1px solid #cbd5e0 !important;
}

.row-label {
  font-weight: 600;
  text-align: center;
  background-color: #f8fafc;
  min-width: 60px;
  font-size: 0.85rem;
  position: sticky;
  left: 0;
  z-index: 20;
}

/* 行表头选中样式 */
.row-label.row-selected {
  background-color: #3182ce !important;
  color: white !important;
}

/* 固定表头样式 */
.sticky-cell {
  position: sticky;
  top: 0;
  z-index: 15;
}

/* 固定左侧列（行表头） */
.midpoint-table tbody td:first-child {
  position: sticky;
  left: 0;
  z-index: 10;
  background-color: #f8fafc;
}

/* 固定左上角单元格 */
.midpoint-table thead tr:first-child th:first-child {
  position: sticky;
  left: 0;
  top: 0;
  z-index: 25;
}

/* 当行被选中时，固定列也要变色 */
.midpoint-table tbody tr:hover td:first-child {
  background-color: #e6f7ff;
}

.selection-hint {
  margin-top: 12px;
  padding: 8px 12px;
  background: #f7fafc;
  border-radius: 4px;
}

.selection-hint p {
  margin: 0;
  font-size: 0.85rem;
  color: #718096;
}

.permission-guard {
  margin: 18px 24px;
  padding: 16px 18px;
  border-radius: 8px;
  background: #fff7e6;
  border: 1px solid #f5c98a;
  color: #8a5a12;
  font-size: 14px;
}

.dialog-footer {
  padding: 8px 24px;
  background: #f8f9fa;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
}

.dialog-buttons {
  display: flex;
  gap: 12px;
}

.btn-confirm, .btn-cancel {
  padding: 6px 14px;
  border-radius: 4px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
}

.btn-confirm {
  background: #3182ce;
  color: white;
  border: 1px solid #3182ce;
}

.btn-cancel {
  background: #f7fafc;
  color: #4a5568;
  border: 1px solid #cbd5e0;
}

@media (max-width: 1150px) {
  .dialog-content {
    width: 95%;
  }

  .midpoint-table {
    min-width: 600px;
  }
}
</style>
